import { haversineM } from './geo';

/** A track point: t is milliseconds from the start of the track. */
export interface TrackPoint {
  lat: number;
  lon: number;
  t: number;
}

/**
 * Minimal GPX parser (trkpt / rtept with optional <time>). No DOM dependency so it runs in
 * Node and in the app. Points without timestamps are spaced by `fallbackSpeedMps`.
 */
export function parseGpx(xml: string, fallbackSpeedMps = 27): TrackPoint[] {
  const re = /<(?:trkpt|rtept)\b([^>]*)>([\s\S]*?)<\/(?:trkpt|rtept)>|<(?:trkpt|rtept)\b([^>]*)\/>/g;
  const raw: { lat: number; lon: number; time: number | null }[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(xml))) {
    const attrs = m[1] ?? m[3] ?? '';
    const body = m[2] ?? '';
    const lat = parseFloat(/lat="([^"]+)"/.exec(attrs)?.[1] ?? '');
    const lon = parseFloat(/lon="([^"]+)"/.exec(attrs)?.[1] ?? '');
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
    const timeStr = /<time>([^<]+)<\/time>/.exec(body)?.[1];
    const time = timeStr ? Date.parse(timeStr) : NaN;
    raw.push({ lat, lon, time: Number.isFinite(time) ? time : null });
  }
  if (!raw.length) return [];

  const out: TrackPoint[] = [];
  const t0 = raw[0]!.time;
  let acc = 0;
  for (let i = 0; i < raw.length; i++) {
    const p = raw[i]!;
    if (t0 != null && p.time != null) {
      out.push({ lat: p.lat, lon: p.lon, t: p.time - t0 });
    } else {
      if (i > 0) {
        const q = raw[i - 1]!;
        acc += (haversineM(q.lat, q.lon, p.lat, p.lon) / fallbackSpeedMps) * 1000;
      }
      out.push({ lat: p.lat, lon: p.lon, t: Math.round(acc) });
    }
  }
  return out;
}

/**
 * Drive along a polyline at a constant speed, emitting one point every `intervalSec` exactly
 * like a 1 Hz GPS would. Works for five hand-picked waypoints (straight lines between them)
 * and for a dense routed shape alike: points are placed by distance along the whole path,
 * so short segments never produce sub-second fixes that starve the heading filter.
 */
export function synthesizeTrack(
  waypoints: { lat: number; lon: number }[],
  { speedMps = 30, intervalSec = 1 }: { speedMps?: number; intervalSec?: number } = {},
): TrackPoint[] {
  if (waypoints.length < 2) return waypoints.map((w) => ({ lat: w.lat, lon: w.lon, t: 0 }));
  const stepM = speedMps * intervalSec;
  const out: TrackPoint[] = [];
  let seg = 0;
  let segStartM = 0;
  let segLenM = haversineM(waypoints[0]!.lat, waypoints[0]!.lon, waypoints[1]!.lat, waypoints[1]!.lon);
  for (let n = 0; ; n++) {
    const target = n * stepM;
    while (target > segStartM + segLenM) {
      segStartM += segLenM;
      seg++;
      if (seg >= waypoints.length - 1) break;
      const a = waypoints[seg]!;
      const b = waypoints[seg + 1]!;
      segLenM = haversineM(a.lat, a.lon, b.lat, b.lon);
    }
    if (seg >= waypoints.length - 1) break;
    const a = waypoints[seg]!;
    const b = waypoints[seg + 1]!;
    const f = segLenM > 0 ? (target - segStartM) / segLenM : 0;
    out.push({ lat: a.lat + (b.lat - a.lat) * f, lon: a.lon + (b.lon - a.lon) * f, t: Math.round(n * intervalSec * 1000) });
  }
  // segStartM is now the whole path length; finish at the final waypoint at the same speed.
  const last = waypoints[waypoints.length - 1]!;
  const prev = out[out.length - 1];
  const tailM = Math.max(0, segStartM - (out.length - 1) * stepM);
  const lastT = (prev?.t ?? 0) + Math.round((tailM / speedMps) * 1000);
  if (!prev || prev.lat !== last.lat || prev.lon !== last.lon) out.push({ lat: last.lat, lon: last.lon, t: Math.max(lastT, (prev?.t ?? -1) + 1) });
  return out;
}
